import { Navigate, useLocation, Link } from "react-router";
import { formatCurrency, formatPaymentMethod } from "@/lib/formatters";
import { useAuth } from "@/lib/auth";
import { ORDER_TYPE_LABELS } from "@/lib/api";
import type { Order, Employee } from "@/lib/api";

interface ConfirmationState {
  order: Order;
  employees: Employee[];
}

export function ConfirmationPage(): React.ReactElement {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const state = location.state as ConfirmationState | null;

  if (!state?.order) {
    return <Navigate to="/" replace />;
  }

  const { order, employees } = state;

  return (
    <div className="mx-auto max-w-[640px] px-4 py-8">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <svg
            className="h-8 w-8 text-green-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.5 12.75l6 6 9-13.5"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Order Confirmed!</h1>
        <p className="mt-2 text-sm text-gray-600">
          Your order has been placed successfully. A confirmation email will be
          sent shortly.
        </p>
      </div>

      <div className="space-y-6 rounded-md border border-gray-200 bg-white p-6">
        <div>
          <h2 className="text-sm font-medium text-gray-500">Order ID</h2>
          <p className="mt-1 text-sm font-mono text-gray-900">{order.id}</p>
        </div>

        <div>
          <h2 className="text-sm font-medium text-gray-500">Product</h2>
          <p className="mt-1 text-sm text-gray-900">
            {ORDER_TYPE_LABELS[order.type ?? "bcb"]}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <h2 className="text-sm font-medium text-gray-500">Total Amount</h2>
            <p className="mt-1 text-lg font-semibold text-gray-900">
              {formatCurrency(order.totalAmount)}
            </p>
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-500">
              Payment Method
            </h2>
            <p className="mt-1 text-sm text-gray-900">
              {formatPaymentMethod(order.paymentMethod)}
              {order.paymentMethodLast4 &&
                ` ending in ${order.paymentMethodLast4}`}
            </p>
          </div>
        </div>

        <div>
          <h2 className="text-sm font-medium text-gray-500">
            Employees ({employees.length})
          </h2>
          <ul className="mt-2 divide-y divide-gray-100">
            {employees.map((emp) => (
              <li key={emp.id} className="py-2 text-sm text-gray-900">
                {emp.firstName} {emp.lastName}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
        <Link
          to={`/orders/${order.id}`}
          className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          View Order Details
        </Link>
        <Link
          to="/orders"
          className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Receipts
        </Link>
        <Link
          // A logged-in vendor adds employees from the roster's checkout, as the
          // nav's New Order does; a public visitor goes back to the public page.
          to={isAuthenticated ? "/roster/new-order" : "/"}
          className="inline-flex items-center justify-center rounded-md bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
        >
          Place Another Order
        </Link>
      </div>
    </div>
  );
}
